import { z } from 'zod';
import { GUARDS } from './enums.js';
import { titleOrderSchema } from './titles.js';
/**
 * The door's wire contract. Shared so the client cannot drift from the server
 * on the one flow where drifting locks everybody out of the site.
 */
export const NICKNAME_MIN = 2;
export const NICKNAME_MAX = 20;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 200;
/**
 * Letters (including Serbian Latin), digits, and a single separator between
 * them. No leading or trailing separator, no runs. Nicknames end up on seat
 * markers and ticket stubs, so they stay short and typeable.
 */
export const nicknameSchema = z
    .string()
    .trim()
    .min(NICKNAME_MIN, 'Bar 2 karaktera.')
    .max(NICKNAME_MAX, 'Najviše 20 karaktera.')
    .regex(/^[\p{L}\p{N}]+(?:[ _-][\p{L}\p{N}]+)*$/u, 'Samo slova, brojevi i po jedan razmak ili crtica između.');
/**
 * Length only. Composition rules (an uppercase! a symbol!) push people toward
 * `Password1!` and away from anything memorable, and this site is protected by
 * an invite code as much as by a password.
 */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN, 'Bar 8 karaktera.')
    .max(PASSWORD_MAX, 'Predugačka.');
export const guardSchema = z.enum(GUARDS);
// --- requests ---------------------------------------------------------------
export const checkCodeRequest = z.object({
    code: z.string().min(1).max(32),
});
export const registerRequest = z.object({
    code: z.string().min(1).max(32),
    nickname: nicknameSchema,
    password: passwordSchema,
});
export const loginRequest = z.object({
    nickname: z.string().trim().min(1).max(NICKNAME_MAX),
    password: z.string().min(1).max(PASSWORD_MAX),
    /**
     * The degraded plain-form door keeps a literal checkbox. When the scene is
     * broken the feature still has to work, so the same endpoint accepts both.
     */
    remember: z.boolean().optional(),
});
/**
 * `guard: null` is pocketing the bill, and it is a first-class answer — not an
 * absent one. Declining must not feel like an error: you get a normal 12-hour
 * session, no nagging, no re-prompt within the session, no penalty. If someone
 * is borrowing a friend's phone to check the seat map, declining has to work.
 */
export const bribeRequest = z.object({
    guard: guardSchema.nullable(),
});
// --- responses --------------------------------------------------------------
export const checkCodeResponse = z.object({
    ok: z.literal(true),
    /** "Miloš" — lets the bouncers greet them before an account exists. */
    intendedForName: z.string(),
    /** Selects an override block in script/vrata.yaml. */
    dialogueKey: z.string().nullable(),
});
export const sessionUserSchema = z.object({
    id: z.string(),
    nickname: z.string(),
    isAdmin: z.boolean(),
    glassesOn: z.boolean(),
    /**
     * Which language you would rather read a film's title in, best first.
     *
     * It rides along with the session for the same reason `glassesOn` does: it
     * changes what is drawn on every surface, so a separate fetch would mean the
     * board renders once in somebody else's order and then corrects itself. See
     * `titles.ts` for how the list is spent.
     */
    titleOrder: titleOrderSchema,
    avatarUrl: z.string().nullable(),
    bribedGuard: guardSchema.nullable(),
});
/**
 * Reordering the sign at the bottom of the page.
 *
 * The whole list travels, not "move sr to position 2": the client already knows
 * the order it is showing, the server does not have to replay a drag, and a
 * request that arrives twice lands on the same answer.
 */
export const titleOrderRequest = z.object({
    order: titleOrderSchema,
});
/**
 * What the door asks on cold entry, before rendering anything.
 *
 * `greeting` is the whole point of the fast path: 'sefe' means this device is
 * on a guard's payroll, the scene is a 1.2-second nod, and no input is
 * required. Everything else means run the real door.
 */
export const sessionResponse = z.object({
    user: sessionUserSchema.nullable(),
    greeting: z.enum(['sefe', 'known', 'stranger']),
    /** Set when this device has never been offered the bill for this session. */
    offerBribe: z.boolean(),
    /** Feeds the bouncers' "you cancelled 3 times, you know" line. */
    lateCancels: z.number().int().nonnegative(),
});
/**
 * The door's rejection shape. `retryAfterSec` drives the in-character
 * countdown ("Vrati se za 9:41") rather than an error toast.
 */
export const doorLockedResponse = z.object({
    code: z.literal('door_locked'),
    retryAfterSec: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map